import type {
  ApiErrorBody, CreateBookingRequest, CreateBookingResponse,
  DocumentKind, DocumentsSession, PaymentMethod, UploadUrlResponse,
} from "./types.js";

export class ApiError extends Error {
  constructor(public code: string, public status: number, message?: string) {
    super(message ?? code);
  }
}

export type UploadUrlRequest = {
  appointment_id: string;
  kind: DocumentKind;
  content_type: string;
  size_bytes: number;
};

export type IntakeApiClient = {
  requestUploadUrl(req: UploadUrlRequest): Promise<UploadUrlResponse>;
  createBooking(req: CreateBookingRequest): Promise<CreateBookingResponse>;
  // Token-scoped endpoints behind the post-booking /documents/<token> page.
  getDocumentsSession(token: string): Promise<DocumentsSession>;
  setPaymentMethod(token: string, method: PaymentMethod): Promise<void>;
  issueDocumentUploadUrl(
    token: string, kind: DocumentKind, file: File,
  ): Promise<Pick<UploadUrlResponse, "document_id" | "sas_url">>;
  commitDocument(token: string, documentId: string, kind: DocumentKind): Promise<void>;
};

function postJson(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  };
}

const docsPath = (token: string) => `/api/v1/documents/${encodeURIComponent(token)}`;

export function buildIntakeApiClient(baseUrl = ""): IntakeApiClient {
  async function request<T>(path: string, init: RequestInit): Promise<T> {
    let res: Response;
    try {
      res = await fetch(`${baseUrl}${path}`, init);
    } catch (cause) {
      throw new ApiError("network_error", 0, (cause as Error).message);
    }
    const text = await res.text();
    const body = text ? JSON.parse(text) : {};
    if (!res.ok) {
      const err = body as ApiErrorBody;
      throw new ApiError(
        err.error?.code ?? "unknown_error",
        res.status,
        err.error?.message,
      );
    }
    return body as T;
  }

  return {
    async requestUploadUrl(req) {
      return request<UploadUrlResponse>("/api/v1/upload-url", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(req),
      });
    },
    async createBooking(req) {
      return request<CreateBookingResponse>("/api/v1/bookings", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(req),
      });
    },
    async getDocumentsSession(token) {
      return request<DocumentsSession>(docsPath(token), { method: "GET" });
    },
    async setPaymentMethod(token, method) {
      await request<unknown>(`${docsPath(token)}/payment-method`, postJson({ paymentMethod: method }));
    },
    async issueDocumentUploadUrl(token, kind, file) {
      return request<Pick<UploadUrlResponse, "document_id" | "sas_url">>(
        `${docsPath(token)}/upload-url`,
        postJson({ kind, content_type: file.type, size_bytes: file.size }),
      );
    },
    async commitDocument(token, documentId, kind) {
      await request<unknown>(`${docsPath(token)}/commit`, postJson({ documentId, kind }));
    },
  };
}
